export interface IInvoiceBilledToCustomer {
	email?: string;
	firstName?: string;
	lastName?: string;
	linkedAccountEmail?: string;
	linkedAccountNames?: Array<string | undefined>;
	linkedAccountPhoneNumber?: string;
	phoneNumber?: string;
}

export interface IInvoiceBilledToContacts {
	email?: string;
	phoneNumber?: string;
}

export interface IInvoiceBillingInfoNameParams {
	customer?: IInvoiceBilledToCustomer;
	obfuscatedCustomer?: IInvoiceBilledToCustomer;
	paymentVariant: boolean;
}

export const getInvoiceBusinessName = (customer?: IInvoiceBilledToCustomer): string | undefined =>
	customer?.linkedAccountNames?.map(name => name?.trim()).find((name): name is string => !!name);

export const getInvoicePersonName = (
	customer?: IInvoiceBilledToCustomer,
	obfuscatedCustomer?: IInvoiceBilledToCustomer
): string =>
	`${customer?.firstName || obfuscatedCustomer?.firstName || ''} ${
		customer?.lastName || obfuscatedCustomer?.lastName || ''
	}`.trim();

export const getInvoiceBilledToName = (
	customer?: IInvoiceBilledToCustomer,
	obfuscatedCustomer?: IInvoiceBilledToCustomer
): string => {
	const businessName = getInvoiceBusinessName(customer);
	if (businessName) {
		return businessName;
	}

	return getInvoicePersonName(customer, obfuscatedCustomer);
};

export const isInvoiceBilledToBusiness = ({
	customer,
	paymentVariant,
}: Pick<IInvoiceBillingInfoNameParams, 'customer' | 'paymentVariant'>): boolean =>
	!paymentVariant && Boolean(getInvoiceBusinessName(customer));

export const getInvoiceBillingInfoName = ({
	customer,
	obfuscatedCustomer,
	paymentVariant,
}: IInvoiceBillingInfoNameParams): string => {
	if (paymentVariant) {
		return getInvoicePersonName(customer, obfuscatedCustomer);
	}

	return getInvoiceBilledToName(customer, obfuscatedCustomer);
};

const trimContact = (value?: string): string | undefined => {
	const trimmed = value?.trim();
	return trimmed || undefined;
};

export const getInvoiceBillingInfoContacts = ({
	customer,
	paymentVariant,
}: Pick<IInvoiceBillingInfoNameParams, 'customer' | 'paymentVariant'>): IInvoiceBilledToContacts => {
	if (isInvoiceBilledToBusiness({ customer, paymentVariant })) {
		return {
			email: trimContact(customer?.linkedAccountEmail),
			phoneNumber: trimContact(customer?.linkedAccountPhoneNumber),
		};
	}

	return {
		email: trimContact(customer?.email),
		phoneNumber: trimContact(customer?.phoneNumber),
	};
};

export const mergeInvoiceCustomerForBilledTo = <T extends object>(
	pickerCustomer: T,
	invoiceCustomer?: IInvoiceBilledToCustomer
): T & IInvoiceBilledToCustomer => ({
	...pickerCustomer,
	linkedAccountEmail: invoiceCustomer?.linkedAccountEmail,
	linkedAccountNames: invoiceCustomer?.linkedAccountNames,
	linkedAccountPhoneNumber: invoiceCustomer?.linkedAccountPhoneNumber,
});
